import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import adminApi from '../../api/adminClient';
import { sidoList, getSigunguList } from '../../data/koreaRegions';
import './Admin.css';

const emptyForm = {
  name: '', clientName: '', regionSido: '', regionSigungu: '', regionDetail: '',
  periodFrom: '', periodTo: '', requiredHeadcount: '', notes: '', status: '사용',
};

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null = 목록, {} = 신규, {...} = 수정
  const [filter, setFilter] = useState('전체');

  useEffect(() => { loadEvents(); }, []);

  async function loadEvents() {
    try {
      setLoading(true);
      const data = await adminApi.get('/events');
      setEvents(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(ev) {
    if (!window.confirm(`"${ev.name}" 업무를 삭제하시겠습니까?\n이 업무와 연결된 근로계약서/문의/지원서는 삭제되지 않고, 연결만 해제됩니다.`)) return;
    try {
      await adminApi.delete(`/events/${ev.id}`);
      loadEvents();
    } catch (err) {
      alert(err.message);
    }
  }

  function isExpired(periodTo) {
    return new Date(periodTo) < new Date(new Date().toDateString());
  }

  const filtered = events.filter(ev => {
    if (filter === '사용') return ev.status === '사용';
    if (filter === '미사용') return ev.status === '미사용';
    return true;
  });

  if (editing !== null) {
    return (
      <AdminLayout>
        <EventForm
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadEvents(); }}
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-header">
        <div>
          <h1>업무 관리</h1>
          <p className="text-muted">등록한 업무는 근로계약서·문의·입사지원에서 선택할 수 있고, 사용자 로그인 시 업무 목록으로도 노출됩니다.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setEditing({})}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          신규 업무 등록
        </button>
      </div>

      <div className="admin-filter-row">
        {['전체', '사용', '미사용'].map(f => (
          <button
            key={f}
            className={`type-filter__btn ${filter === f ? 'type-filter__btn--active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f} {f !== '전체' && `(${events.filter(ev => ev.status === f).length})`}
          </button>
        ))}
      </div>

      {loading && <div className="state-message">불러오는 중입니다...</div>}
      {!loading && error && <div className="state-message state-message--error">{error}</div>}

      {!loading && !error && (
        filtered.length === 0 ? (
          <div className="state-message">조건에 맞는 업무가 없습니다.</div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>상태</th>
                  <th>업무명</th>
                  <th>발주자</th>
                  <th>업무지역</th>
                  <th>업무기간</th>
                  <th>필요인원</th>
                  <th style={{ width: 160 }}>관리</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(ev => (
                  <tr key={ev.id} className={ev.status === '미사용' ? 'admin-table__row--inactive' : ''}>
                    <td>
                      <span className={`badge ${ev.status === '사용' ? 'badge-green' : 'badge-red'}`}>{ev.status}</span>
                      {isExpired(ev.periodTo) && <span className="badge badge-accent" style={{ marginLeft: 6 }}>기간만료</span>}
                    </td>
                    <td className="admin-table__title">{ev.name}</td>
                    <td>{ev.clientName}</td>
                    <td>{ev.regionSido} {ev.regionSigungu}</td>
                    <td>{ev.periodFrom} ~ {ev.periodTo}</td>
                    <td>{ev.requiredHeadcount ? `${ev.requiredHeadcount}명` : '-'}</td>
                    <td className="admin-table__actions">
                      <button className="admin-action-btn" onClick={() => setEditing(ev)}>수정</button>
                      <button className="admin-action-btn admin-action-btn--danger" onClick={() => handleDelete(ev)}>삭제</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </AdminLayout>
  );
}

/* ── 등록/수정 폼 ── */
function EventForm({ initial, onCancel, onSaved }) {
  const isEdit = !!initial.id;
  const [form, setForm] = useState(() => {
    if (!isEdit) return emptyForm;
    return {
      name: initial.name || '',
      clientName: initial.clientName || '',
      regionSido: initial.regionSido || '',
      regionSigungu: initial.regionSigungu || '',
      regionDetail: initial.regionDetail || '',
      periodFrom: initial.periodFrom || '',
      periodTo: initial.periodTo || '',
      requiredHeadcount: initial.requiredHeadcount || '',
      notes: initial.notes || '',
      status: initial.status || '사용',
    };
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'regionSido') {
      setForm({ ...form, regionSido: value, regionSigungu: '' });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.clientName || !form.regionSido || !form.regionSigungu || !form.periodFrom || !form.periodTo) {
      setError('업무명, 발주자, 업무 지역(시/도, 시/군/구), 업무 기간은 필수입니다.');
      return;
    }
    if (new Date(form.periodFrom) > new Date(form.periodTo)) {
      setError('업무 시작일은 종료일보다 늦을 수 없습니다.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await adminApi.put(`/events/${initial.id}`, form);
      } else {
        await adminApi.post('/events', form);
      }
      onSaved();
    } catch (err) {
      setError(err.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const sigunguOptions = getSigunguList(form.regionSido);

  return (
    <div>
      <button className="back-btn" onClick={onCancel}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
        목록으로
      </button>

      <div className="admin-form-card">
        <h2>{isEdit ? '업무 정보 수정' : '신규 업무 등록'}</h2>

        <form onSubmit={handleSubmit}>
          {error && <div className="form-error">{error}</div>}

          <div className="form-group">
            <label>업무명 <span className="required">*</span></label>
            <input name="name" value={form.name} onChange={handleChange} placeholder="예: 2026 봄 페스티벌 운영 지원" />
          </div>
          <div className="form-group">
            <label>발주자 <span className="required">*</span></label>
            <input name="clientName" value={form.clientName} onChange={handleChange} placeholder="예: ○○구청, ○○주식회사" />
          </div>

          <h4 className="admin-form-section-title">업무 지역</h4>
          <div className="form-row-2">
            <div className="form-group">
              <label>시/도 <span className="required">*</span></label>
              <select name="regionSido" value={form.regionSido} onChange={handleChange}>
                <option value="">선택</option>
                {sidoList.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>시/군/구 <span className="required">*</span></label>
              <select name="regionSigungu" value={form.regionSigungu} onChange={handleChange} disabled={!form.regionSido}>
                <option value="">선택</option>
                {sigunguOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>업무 지역 상세</label>
            <input name="regionDetail" value={form.regionDetail} onChange={handleChange} placeholder="예: ○○컨벤션센터 3층" />
          </div>

          <h4 className="admin-form-section-title">업무 기간 및 규모</h4>
          <div className="form-row-2">
            <div className="form-group">
              <label>시작일 <span className="required">*</span></label>
              <input type="date" name="periodFrom" value={form.periodFrom} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>종료일 <span className="required">*</span></label>
              <input type="date" name="periodTo" value={form.periodTo} onChange={handleChange} />
            </div>
          </div>
          <div className="form-group">
            <label>필요 인원</label>
            <input type="number" name="requiredHeadcount" value={form.requiredHeadcount} onChange={handleChange} placeholder="예: 15" inputMode="numeric" min="1" />
          </div>
          <div className="form-group">
            <label>기타 사항</label>
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="참고할 내용이 있으면 입력하세요." />
          </div>

          <div className="form-group">
            <label>상태 <span className="required">*</span></label>
            <select name="status" value={form.status} onChange={handleChange}>
              <option value="사용">사용</option>
              <option value="미사용">미사용</option>
            </select>
            <p className="my-login__hint" style={{ marginTop: '0.4rem' }}>
              "사용" + 종료일이 지나지 않은 업무만 사용자 로그인 화면의 선택 목록에 노출됩니다.
            </p>
          </div>

          <div className="admin-form-actions">
            <button type="button" className="btn btn-outline-navy" onClick={onCancel}>취소</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '저장 중...' : isEdit ? '수정 완료' : '등록하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
